import { useEffect, useRef } from 'react'

import type { ChartApi, LineHandle } from '@/components/PriceChart'
import { computeLines, type Indicator } from '@/lib/indicators'
import type { Candle } from '@/lib/types'

/**
 * Pose les indicateurs sur le graphique, et les y maintient.
 *
 * Ce composant ne rend AUCUN élément : il pilote des courbes appartenant à
 * `lightweight-charts`. C'est la différence avec le calque des niveaux, qui
 * est du HTML positionné au-dessus du canevas. Une poignée de stop a besoin
 * d'une cible tactile de 44 px ; une moyenne mobile a besoin de suivre trois
 * mille points au défilement. Les deux ne se dessinent pas au même endroit.
 *
 * Les courbes sont RÉUTILISÉES d'un rendu à l'autre, par clé. Les détruire et
 * les recréer à chaque nouvelle bougie ferait clignoter le graphique une fois
 * par seconde, et perdrait le lissage de la bibliothèque.
 */
export function ChartIndicators({
  api,
  candles,
  indicators,
}: {
  api: ChartApi | null
  candles: Candle[]
  indicators: Indicator[]
}) {
  // Courbes vivantes, par clé. Une référence et non un état : y toucher ne
  // doit jamais provoquer de rendu, c'est le graphique qui se met à jour.
  const lines = useRef(new Map<string, LineHandle>())
  // Ce qui a été transmis la dernière fois, pour reconnaître une cotation qui
  // n'a fait bouger que la dernière bougie.
  const drawn = useRef<{ candles: Candle[]; indicators: Indicator[] } | null>(null)

  useEffect(() => {
    const open = lines.current
    if (!api) {
      // Le graphique a disparu (changement de paire, démontage) : les poignées
      // qu'on garde ne désignent plus rien. On les oublie sans les retirer —
      // leur graphique est déjà détruit.
      open.clear()
      drawn.current = null
      return
    }

    /*
     * Seule la dernière bougie a bougé ? Alors seul le dernier point de
     * chaque courbe a bougé : une moyenne, un écart-type, une exponentielle ne
     * dépendent que des bougies passées et de la dernière. On recalcule tout —
     * c'est l'affaire d'une milliseconde — mais on ne transmet au graphique
     * que ce dernier point. Recharger les courbes entières à chaque cotation,
     * trois pour une seule bande de Bollinger, faisait plusieurs dizaines de
     * milliers de points par seconde sur un téléphone.
     */
    const before = drawn.current
    drawn.current = { candles, indicators }
    const anchor = candles.length - 2
    const growth = before ? candles.length - before.candles.length : -1
    const onlyTheEndMoved =
      before !== null &&
      before.indicators === indicators &&
      (growth === 0 || growth === 1) &&
      anchor >= 0 &&
      candles[anchor] === before.candles[anchor]

    const wanted = indicators.flatMap((indicator) => computeLines(indicator, candles))
    const seen = new Set<string>()

    for (const line of wanted) {
      seen.add(line.key)
      const options = { color: line.color, width: line.width, dashed: line.dashed }
      let handle = open.get(line.key)
      if (!handle) {
        const created = api.addLine(options)
        if (!created) continue
        handle = created
        open.set(line.key, handle)
      } else if (onlyTheEndMoved) {
        const last = line.points.at(-1)
        if (last) handle.update(last)
        continue
      } else {
        // Couleur ou épaisseur changées dans le panneau : on ajuste la courbe
        // existante plutôt que d'en refaire une.
        handle.applyOptions(options)
      }
      handle.setData(line.points)
    }

    // Indicateur supprimé, ou période devenue trop longue pour les bougies
    // chargées : sa courbe doit disparaître, pas rester figée sur ses
    // dernières valeurs.
    for (const [key, handle] of open) {
      if (seen.has(key)) continue
      handle.remove()
      open.delete(key)
    }
  }, [api, candles, indicators])

  // Démontage : on rend ses courbes au graphique.
  useEffect(() => {
    const open = lines.current
    return () => {
      for (const handle of open.values()) handle.remove()
      open.clear()
    }
  }, [])

  return null
}
